import { AdminApi } from "../../../services/admin-api";
import { applicationLogger } from "../../../logging/logger";
import { autoinject, computedFrom, observable, TaskQueue } from 'aurelia-framework';
import * as moment from "moment";
import { MdToastService, MdModal, MdCollapsible } from "aurelia-materialize-bridge";
import { OpenIdConnect } from "aurelia-open-id-connect";
import { Page } from "../../common/abstract-page";
import { Kiosk } from "../../../models/kiosk";
import { ProcessedTransactionRequest } from "../../../models/processed-transaction-request";
import { TransactionBatchesRequest } from "../../../models/transaction-batches-request";
import { Router } from "aurelia-router";
import { TenantService } from "../../../services/tenant-service";
import { Transaction } from "../../../models/transaction";
import { TransactionBatch } from "../../../models/transaction-batch";
import { TransactionDetail, TransactionResult } from "../../../models/transaction-detail";
import { TransactionStatus } from "../../../models/transaction-status-enum";
import { ProcessedTransactionByLastFourRequest } from "../../../models/processed-transaction-by-last-four-request";

@autoinject
export class TransactionsLookup extends Page {

    initialized: boolean;
    tenantId: string;
    tenantName: string;

    kiosks: Array<Kiosk>;
    selectedKiosk: Kiosk;
    transactions: Array<Transaction>;
    selectedTransaction: Transaction;
    transactionDetail: TransactionDetail;
    refundAmount: string;
    batches: Array<TransactionBatch>;
    selectedBatch: TransactionBatch;

    collapsible: MdCollapsible;

    @computedFrom('transactions')
    get totalTransactions(): number {
        return this.transactions ? this.transactions.length : 0;
    }

    @computedFrom('transactions')
    get unprocessedTransactions(): number {
        return this.transactions ? this.transactions.filter(t => t.transactionStatus === 2).length : 0;
    }

    @computedFrom('transactions')
    get processedTransactions(): number {
        return this.transactions ? this.transactions.filter(t => t.transactionStatus === 3).length : 0;
    }

    @computedFrom('transactions')
    get declinedTransactions(): number {
        return this.transactions ? this.transactions.filter(t => t.transactionStatus === 4).length : 0;
    }

    @computedFrom('transactions')
    get faultedTransactions(): number {
        return this.transactions ? this.transactions.filter(t => t.transactionStatus === 5).length : 0;
    }

    @computedFrom('transactions')
    get refundedTransactions(): number {
        return this.transactions ? this.transactions.filter(t => t.transactionStatus === 6).length : 0;
    }

    @computedFrom('transactions')
    get partiallyRefundedTransactions(): number {
        return this.transactions ? this.transactions.filter(t => t.transactionStatus === 10).length : 0;
    }

    @computedFrom('transactions')
    get serverErrorTransactions(): number {
        return this.transactions ? this.transactions.filter(t => t.transactionStatus === 11).length : 0;
    }

    transactionFilters = [
        { value: '', keys: ['lastFour'] }, // this doesn't seem to work with pagination enabled.'
        { value: '', custom: this.customFilter} // this does work with pagination enabled.
    ];

    customFilter(filterValue: string, row : any) {
        return (row.cardHolder.includes(filterValue) || row.lastFour.includes(filterValue))
    }

    @observable startDate: string;
    @observable endDate: string;
    @observable days: any;
    rangeSelection: any;

    @observable resubmitStartDate: string;
    @observable resubmitEndDate: string;
    selectedTransactionStatus: TransactionStatus = TransactionStatus.Offline;

    @observable transactionBatchesStartDate: string;
    @observable transactionBatchesEndDate: string;
    @observable batchesDays: any;
    batchesRangeSelection: any;



    transactionDetailsModal: MdModal;
    processTransactionModal: MdModal;
    processRefundModal: MdModal;
    transactionStatusModal: MdModal;
    resubmitTransactionsModal: MdModal;
    accordion: MdCollapsible;

    initializing: boolean;

    defaultButton: HTMLButtonElement;

    attached() {
        window.addEventListener('keydown', this.handleKey);
    }

    detached() {
        window.removeEventListener('keydown', this.handleKey);
    }

    handleKey = (e: KeyboardEvent) => {
        if (e.key === 'Enter') {
            this.defaultButton?.click();
        }
    };



    get fetching() {
          return this.initializing || this.adminApi.fetching;
    }


    // NB: current-page and page-size are required by aurelia-table
    // Typescript is requiring the member names to be esacped in quotes due
    // to the '-' character.
    currentPage: number = 0;
    pageSize: number = 10;

    @computedFrom('transactionDetail')
    get showPurchaseDetailsSection(): boolean {
        return this.transactionDetail && this.transactionDetail.purchaseDetailsData !== null;
    }

    @computedFrom('transactionDetail')
    get showProcessResultSection(): boolean {
        return this.transactionDetail && this.transactionDetail.status > 2;
    }

    @computedFrom('transactionDetail')
    get showProcessResultText(): boolean {
        return this.transactionDetail && this.transactionDetail.processResult && ! this.transactionDetail.processResult.startsWith("{");
    }

    @computedFrom('transactionDetail')
    get showRefundResultSection(): boolean {
        return this.transactionDetail && (this.transactionDetail.status === TransactionStatus.Refunded || this.transactionDetail.status === TransactionStatus.RefundFailed || this.transactionDetail.status === TransactionStatus.PartiallyRefunded);
    }

    @computedFrom('transactionDetail')
    get showRefundResultText(): boolean {
        return this.transactionDetail && this.transactionDetail.result && this.transactionDetail.result.RefundResult && ! this.transactionDetail.result.RefundResult.startsWith("{");
    }
    
    @computedFrom('transactionDetail')
    get showLogsSection(): boolean {
        return this.transactionDetail && (this.transactionDetail.logEntries.length > 0);
    }

    @computedFrom('transactionDetail')
    get showMobileTimestamp(): boolean {
        return false;
        //return this.transactionDetail && ! moment(this.transactionDetail.transactionData.transactionTimestamp).isSame(this.transactionDetail.mobileTimestamp, 'minute');
    }
    
    constructor(adminApi: AdminApi,
        toast: MdToastService,
        router: Router,
        openIdConnect: OpenIdConnect,
        private tenantService: TenantService,
        private taskQueue: TaskQueue
        ) {

        super(adminApi, toast, router, openIdConnect);

        this.rangeSelection = [
            { name: "Today", value: 0 },
            { name: "This week", value: -6 + moment().day() },
            { name: "7 days", value: -6 },
            { name: "30 days", value: -29 },
            { name: "MTD", value: -moment().date() + 1 },
            { name: "365 days", value: -364 },
            { name: "YTD", value: -moment().dayOfYear() + 1 }
        ];
        this.days = this.rangeSelection[0];

        this.batchesRangeSelection = [
            { name: "Today", value: 0 },
            { name: "This week", value: -6 + moment().day() },
            { name: "7 days", value: -6 },
            { name: "30 days", value: -29 },
            //{ name: "MTD", value: -moment().date() + 1 },
            //{ name: "365 days", value: -364 },
            //{ name: "YTD", value: -moment().dayOfYear() + 1 }
        ];
        this.batchesDays = this.batchesRangeSelection[2];
       
        
    }

    configureKioskData(kiosks: Kiosk[], params: any) 
    {
        this.kiosks = kiosks;

        // **************************************************************************
        // to prevent transaction data from being fetched when the page first loads
        // unremark, then set this.selectedKiosk = selectKiosk on line 186

        //const selectKiosk = new Kiosk();
        //selectKiosk.name = "(Select kiosk)";
        //selectKiosk.id = null;
        //this.kiosks.unshift(selectKiosk)

        // **************************************************************************

        const allKiosks = new Kiosk();
        allKiosks.name = "(All kiosks)";
        allKiosks.id = null;
        this.kiosks.push(allKiosks);

        if (params.kioskId) {
            this.selectedKiosk = (this.kiosks.find((kiosk): boolean => kiosk.id === params.kioskId)) as Kiosk;
        }
        else {
            this.selectedKiosk = allKiosks;
        }
    }

    configureBatchData(batches: TransactionBatch[]) {

        this.batches = batches;
        const selectBatch = new TransactionBatch();
        selectBatch.name = "(Select a transaction batch)";
        selectBatch.id = null;
        this.batches.unshift(selectBatch);
    }

    async activate(params: any) {
        this.tenantId = params.tenantId;
        this.tenantName = this.tenantService.tenantName;

     
        try {
            this.initializing = true;
            this.transactions = new Array<Transaction>();
            this.initialized = true;
           
        }
        finally {

           
            this.initializing = false;
        }

           
      
        // used in the resubmitTransactionsModal dialog
        this.resubmitStartDate = moment().subtract(1, 'months').startOf('month').hours(0).minutes(0).seconds(0).format('YYYY-MM-DD HH:mm:ss');
        this.resubmitEndDate = moment().utc().subtract(1, 'months').endOf('month').hours(0).minutes(0).seconds(0).format('YYYY-MM-DD HH:mm:ss');

        // queue the opening of the md-collapsible element.
        this.taskQueue.queueTask(() => this.collapsible.open());
    }

    collapsibleOpen(targetElement) {
        targetElement.openCount = (targetElement.openCount || 0) + 1;
    }

    startDateChanged() {
        this.startDate = moment(this.startDate).hours(0).minutes(0).seconds(0).format("YYYY-MM-DD HH:mm:ss");

        if (this.initialized && !this.adminApi.fetching) {
            if (moment(this.startDate).isBefore(moment(this.endDate))) {
                this.fetchTransactionData();
            }
        }
    }

    endDateChanged() {
        if (this.initialized) {
            this.endDate = moment(this.endDate).hours(23).minutes(59).seconds(59).format("YYYY-MM-DD HH:mm:ss");
            if (this.initialized && !this.adminApi.fetching) {
                if (moment(this.startDate).isBefore(moment(this.endDate))) {
                    this.fetchTransactionData();
                }
            }
        }
    }

    transactionBatchesStartDateChanged() {
        this.transactionBatchesStartDate = moment(this.transactionBatchesStartDate).hours(0).minutes(0).seconds(0).format("YYYY-MM-DD HH:mm:ss");

        if (this.initialized && !this.adminApi.fetching) {
            if (moment(this.startDate).isBefore(moment(this.endDate))) {
                this.fetchTransactionBatches();
            }
        }
    }

    transactionBatchesEndDateChanged() {
        if (this.initialized) {
            this.transactionBatchesEndDate = moment(this.transactionBatchesEndDate).hours(23).minutes(59).seconds(59).format("YYYY-MM-DD HH:mm:ss");
            if (this.initialized && !this.adminApi.fetching) {
                if (moment(this.transactionBatchesStartDate).isBefore(moment(this.transactionBatchesEndDate))) {
                    this.fetchTransactionBatches();
                }
            }
        }
    }

    daysChanged() {
        const end = moment().hours(23).minutes(59).seconds(59);
        this.endDate = end.format("YYYY-MM-DD HH:mm:ss"); 
        const start = moment().hours(0).minutes(0).seconds(0).add(moment.duration(this.days.value, "day"));
        this.startDate = start.format("YYYY-MM-DD HH:mm:ss");
        //this.fetchTransactionData();
    }

    batchesDaysChanged() {
        const end = moment().hours(23).minutes(59).seconds(59);
        this.transactionBatchesEndDate = end.format("YYYY-MM-DD HH:mm:ss");
        const start = moment().hours(0).minutes(0).seconds(0).add(moment.duration(this.batchesDays.value, "day"));
        this.transactionBatchesStartDate = start.format("YYYY-MM-DD HH:mm:ss");
        this.fetchTransactionBatches();
    }

    kioskChanged() {
        this.fetchTransactionData();
    }

    async batchChanged() {
        if (this.initialized && this.selectedBatch.id) {
            applicationLogger.debug("fetching transaction batch data");
            await this.getTransactionBatchData(this.selectedBatch.id);
            applicationLogger.debug("fetched transaction batch data");
        }
    }

    async fetchTransactionData() {
        if (this.initialized)
        {
            applicationLogger.debug("fetching transaction data");
            await this.getTransactionData(this.createTransactionRequest());
            applicationLogger.debug("fetched transaction data");
        }
    }

    async fetchTransactionBatches() {
        if (this.initialized) {
            applicationLogger.debug("fetching transaction data");
            await this.getTransactionBatches(this.createTransactionBatchesRequest());
            applicationLogger.debug("fetched transaction data");
        }
    }

    //async fetchDataCsv() {
    //    if (this.initialized) {
    //        applicationLogger.debug("fetching transaction csv data");
    //        await this.fetchTransactionDataCsv(this.createTransactionRequest());
    //        applicationLogger.debug("fetched transactioncsv data");
    //    }
    //}

    async resubmitDeclined() {

    }
  
    createTransactionRequest(): ProcessedTransactionByLastFourRequest {
        const viewModel = new ProcessedTransactionByLastFourRequest();
        viewModel.lastFour = this.transactionFilters[0].value;
        viewModel.startDate = moment(this.startDate).toISOString(true);
        viewModel.endDate = moment(this.endDate).toISOString(true); 

        return viewModel;
    }

    createTransactionBatchesRequest(): TransactionBatchesRequest {
        const viewModel = new TransactionBatchesRequest();
        viewModel.tenantId = this.tenantId;
        viewModel.startDate = moment(this.transactionBatchesStartDate).toISOString(true);
        viewModel.endDate = moment(this.transactionBatchesEndDate).toISOString(true);

        return viewModel;
    }

    async getTransactionBatches(viewModel: TransactionBatchesRequest) {
        const response = await this.adminApi.getTransactionBatchesByDates(viewModel);
        if (response.success && response.data) {

            this.configureBatchData(response.data);
        } else {
            this.toast.show(response.message, 5000, "red");
            applicationLogger.error(response.message);
        }
    }

    async getTransactionData(viewModel: ProcessedTransactionByLastFourRequest) {
        const response = await this.adminApi.getProcessedTransactionsByLastFour(viewModel);
        if (response.success && response.data) {
            this.transactions = response.data;
        } else {
            this.toast.show(response.message, 5000, "red");
            applicationLogger.error(response.message);
        }
    }

    async fetchTransactionDataCsv(viewModel: ProcessedTransactionRequest) {
        var response = await this.adminApi.getProcessedTransactionsCsv(viewModel, this.selectedKiosk);
        if (!response.success) {
            this.toast.show(response.message, 5000, "red");
            applicationLogger.error(response.message);
        }
    }

    async getTransactionBatchData(batchId: string) {
        const response = await this.adminApi.getTransactionBatch(batchId);
        if (response.success && response.data) {
            this.transactions = response.data;
        } else {
            this.toast.show(response.message, 5000, "red");
            applicationLogger.error(response.message);
        }
    }

    async openTransactionDetailsModal(id: string) {
        this.selectedTransaction = (this.transactions.find((transaction): boolean => transaction.id === id)) as Transaction;
        const response = await this.adminApi.getTransactionDetail(id);
        if (response.success) {
            this.transactionDetail = response.data;
            this.transactionDetail.result = this.transactionDetail.processResult && this.transactionDetail.processResult.startsWith("{") ? JSON.parse(this.transactionDetail.processResult) : new TransactionResult();
            if (this.transactionDetail.result.RefundResult && this.transactionDetail.result.RefundResult.startsWith("{")) {
                this.transactionDetail.refundResult = JSON.parse(this.transactionDetail.result.RefundResult);
            }
            if (this.transactionDetail.purchaseDetails && this.transactionDetail.purchaseDetails.startsWith("{")) {
                this.transactionDetail.purchaseDetailsData = JSON.parse(this.transactionDetail.purchaseDetails);
            }
            this.transactionDetailsModal.open();
        } else {
            this.toast.show(response.message, 5000, "red");
        }
    }

    async closeTransactionDetails() {
        this.accordion.close();
        this.transactionDetailsModal.close();
    }

    notifyTransactionIdCopy(transactionId: string) {
        this.toast.show(`Copied transaction ID: '${transactionId}' to clipboard.`);
    }

    async downloadLogFile(logName: string) {
        await this.adminApi.downloadTransactionLog(this.selectedTransaction.id, logName, this.selectedTransaction.tenantId);
    }
    
    async openProcessRefundModal(id: string) {
        this.selectedTransaction = (this.transactions.find((transaction): boolean => transaction.id === id)) as Transaction;
        this.refundAmount = this.selectedTransaction.transactionAmount.toFixed(2);
        this.processRefundModal.open();
    }

    async processRefund() {
        const response = await this.adminApi.processRefund(this.selectedTransaction.id, this.refundAmount);
        if (response) {
            if (response.success) {
                this.toast.show(response.message);
                // NB: Remarking for now until we wire in SignalR.
                //this.selectedTransaction.transactionStatus = response.data.transactionStatus;
                //if (response.data.transactionStatus === TransactionStatus.PartiallyRefunded) {
                //    this.selectedTransaction.netAmount = response.data.netAmount;
                //}
                this.selectedTransaction.isProcessed = false;
            } else {
                this.toast.show(response.message, 5000, "red");
            }
        }
        this.processRefundModal.close();
    }

    async cancelProcessRefund() {
        this.processRefundModal.close();
    }

    async openProcessTransactionModal(id: string) {
        this.selectedTransaction = (this.transactions.find((transaction): boolean => transaction.id === id)) as Transaction;
        this.processTransactionModal.open();
    }

    async processTransaction() {
        const response = await this.adminApi.processTransaction(this.selectedTransaction.id);
        if (response.success) {
            this.toast.show(response.message);
            this.processTransactionModal.close();
        } else {
            this.toast.show(response.message, 5000, "red");
        }

        const index = this.transactions.indexOf(this.selectedTransaction);
        this.transactions[index] = response.data;
    }

    async cancelProcessTransaction() {
        this.processTransactionModal.close();
    }

    async openTransactionStatusModal(id: string) {
        this.selectedTransaction = (this.transactions.find((transaction): boolean => transaction.id === id)) as Transaction;
        this.transactionStatusModal.open();
    }

    async updateTransactionStatus() {
        const response = await this.adminApi.updateTransactionStatus(this.selectedTransaction.id, this.selectedTransactionStatus);
        if (response.success) {
            const index = this.transactions.indexOf(this.selectedTransaction);
            this.transactions[index].transactionStatus = response.data;
            this.transactions[index].isResubmitable = response.data === TransactionStatus.Rejected;

            this.toast.show(response.message);
            this.transactionStatusModal.close();
        } else {
            this.toast.show(response.message, 5000, "red");
        }

    }

    async cancelUpdateTransactionStatus() {
        this.transactionStatusModal.close();
    }

    async openResubmitTransactionsModal() {
      this.resubmitTransactionsModal.open();
    }

    async resubmitTransactions() {
      const response = await this.adminApi.resubmitTransactions(this.tenantId, this.resubmitStartDate, this.resubmitEndDate);
      if (response.success) {
        this.toast.show(response.message);
        this.resubmitTransactionsModal.close();
      } else {
        this.toast.show(response.message, 5000, "red");
      }
    }

    async cancelResubmitTransactions() {
      this.resubmitTransactionsModal.close();
    }
}